import { apiFetch } from '../../../auth/apiFetch';

// Room-level light control for the kiosk.
//
// Design (agreed 2026-09-25): the kiosk controls ROOMS, never individual
// bulbs. Three levels — everything off, a floor off, a room on/off — and the
// rooms double as the map's "which lights are on" layer.
//
// Why a registry here instead of Home Assistant areas: HA only has three
// areas defined and exactly one entity assigned to one of them, and the
// device names that arrive from SmartLife / Kasa ("Small Room 1") don't say
// which room they're in. The mapping lives here, next to the floor plan it
// has to agree with, and is edited by hand like the sensor coordinates.
//
// Mixed domains are expected and fine: a room can hold `light.*` bulbs and
// `switch.*` wall switches / smart plugs at once. An entity that's
// `unavailable` (the Meross lamps still boxed up after the move, a bulb cut
// at its wall switch) counts as off and starts working the moment it comes
// online — no code change needed.

export type LightFloorId = 'upper' | 'main' | 'basement';

export interface LightRoom {
  key: string;
  label: string;
  // null = not yet mapped to a floor. The device name gave no clue and it
  // hasn't been confirmed, so the room still works (and is still swept by
  // All off) but lists under "Unassigned" instead of a floor.
  floor: LightFloorId | null;
  entities: string[];
  // Left alone by All off and by the floor switches. The Ring floodlights are
  // motion-triggered security lighting — killing the house lights at bedtime
  // shouldn't blind the cameras.
  excludeFromBulkOff?: boolean;
}

// Deliberately NOT surfaced: the `switch.*_led` entities that come with each
// Kasa HS200 (they control the little indicator LED on the physical switch,
// not the load) and ring-mqtt's duplicate `light.*_light_2` floodlight
// entities (the built-in Ring integration's copies are the ones used here).
export const LIGHT_ROOMS: LightRoom[] = [
  // ---- Main floor ----
  // The two sink bulbs are a separate fixture but the same room to a person,
  // so they switch with the rest of the kitchen.
  {
    key: 'kitchen',
    label: 'Kitchen',
    floor: 'main',
    entities: [
      'light.kitchen_1',
      'light.kitchen_2',
      'light.kitchen_3',
      'light.kitchen_4',
      'light.kitchen_sink_1',
      'light.kitchen_sink_2',
    ],
  },
  {
    key: 'dining',
    label: 'Dining Room',
    floor: 'main',
    // Dining Room 1-3 and the Fireplace are Govee, commissioned over Matter
    // (they pair over Bluetooth, not mDNS — the H6013 never advertises
    // _matterc on the LAN). 4 and 5 are Tuya. The Govee bulbs bottom out at
    // 2695K, so STANDARD_KELVIN (2000) clamps for them and this room reads
    // slightly two-toned.
    entities: [
      'light.dining_room_1',
      'light.dining_room_2',
      'light.dining_room_3',
      'light.dining_room_4',
      'light.dining_room_5',
      'light.dining_room_fireplace',
    ],
  },
  {
    key: 'pantry',
    label: 'Pantry',
    floor: 'main',
    entities: ['light.pantry_1', 'light.pantry_2', 'light.pantry_3'],
  },
  {
    key: 'powder_room',
    label: 'Powder Room',
    floor: 'main',
    entities: ['light.powder_room_1', 'light.powder_room_2', 'light.powder_room_3'],
  },
  {
    key: 'entry_way',
    label: 'Entry',
    floor: 'main',
    entities: ['light.entry_way_1', 'light.entry_way_2', 'light.entry_way_3'],
  },
  {
    key: 'laundry',
    label: 'Laundry',
    floor: 'main',
    entities: ['light.laundry_room_1', 'light.laundry_room_2'],
  },

  // ---- Upper floor ----
  // Layout, per Dakota reading the flat map (2026-09-25): Master Bedroom is
  // the room at the top left with its bathroom beside it; the angled wing down
  // the left side runs Office (far left), then the Upstairs Guest Room below
  // it, then the Small Room, with the upstairs bathroom across from those.
  // The open area on the right is the vaulted ceiling over the main-floor
  // living room — no room and no lights.
  { key: 'master', label: 'Master Bedroom', floor: 'upper', entities: ['switch.master_bedroom'] },
  // Master Bathroom 3 is the bulb whose half-finished pairing took the whole
  // Tuya integration down on 2026-09-27; it was re-paired and renamed.
  {
    key: 'master_bath',
    label: 'Master Bath',
    floor: 'upper',
    entities: [
      'light.master_bathroom_1',
      'light.master_bathroom_2',
      'light.master_bathroom_3',
      'light.master_bathroom_4',
      'light.master_bathroom_5',
      'light.master_bathroom_6',
      'light.master_bathroom_7',
      'light.master_bathroom_8',
    ],
  },
  {
    key: 'master_closet',
    label: 'Master Closet',
    floor: 'upper',
    entities: ['light.master_closet_1', 'light.master_closet_2'],
  },
  // Kasa wall switch for the ceiling lights. The desk and floor lamps
  // (switch.office_desk_lamp / office_floor_lamp) follow it via the HA
  // automation `office_lamps_follow_switch`, so they need no entry here.
  // switch.small_desk_lamp / large_desk_lamp were dropped 2026-10-09: offline
  // Meross plugs awaiting a new job.
  {
    key: 'office',
    label: 'Office',
    floor: 'upper',
    entities: ['switch.office'],
  },
  // The UPSTAIRS guest room. The basement has its own, below.
  {
    key: 'guest_room_2',
    label: 'Upstairs Guest Room',
    floor: 'upper',
    entities: ['light.guest_bedroom_2'],
  },
  // "Small Room" is what Dakota calls this room, not a vendor placeholder.
  {
    key: 'small_room',
    label: 'Small Room',
    floor: 'upper',
    entities: [
      'light.small_room_1',
      'light.small_room_2',
      'light.small_room_3',
      'light.small_room_4',
    ],
  },
  {
    key: 'upstairs_bath',
    label: 'Upstairs Bath',
    floor: 'upper',
    entities: [
      'light.upstairs_bathroom_1',
      'light.upstairs_bathroom_2',
      'light.upstairs_bathroom_3',
    ],
  },
  {
    key: 'upstairs_hall',
    label: 'Hallway',
    floor: 'upper',
    entities: [
      'light.upstairs_hallway_1',
      'light.upstairs_hallway_2',
      'light.upstairs_hallway_3',
    ],
  },

  // ---- Basement ----
  // Natalia works at a desk in here. NOTHING may auto-switch these off — see
  // docs/motion-lighting.md. The basement motion sensor drives the HALLWAY
  // lights only, never this room.
  {
    key: 'basement',
    label: 'Basement',
    floor: 'basement',
    entities: [
      'light.basement_1',
      'light.basement_2',
      'light.basement_3',
      'light.basement_4',
      'light.basement_5',
      'light.basement_6',
    ],
  },
  {
    key: 'basement_hall',
    label: 'Hallway',
    floor: 'basement',
    entities: [
      'light.basement_hallway_1',
      'light.basement_hallway_2',
      'light.basement_hallway_3',
      'light.basement_hallway_4',
    ],
  },
  // The room wearing the guest_window_* contact sensors on the security map.
  {
    key: 'basement_guest',
    label: 'Basement Guest Room',
    floor: 'basement',
    entities: [
      'light.basement_guest_bedroom_1',
      'light.basement_guest_bedroom_2',
      'light.basement_guest_bedroom_3',
    ],
  },
  { key: 'basement_stairs', label: 'Stairs', floor: 'basement', entities: ['light.basement_stairs'] },
  { key: 'storage', label: 'Storage', floor: 'basement', entities: ['light.storage_room'] },

  // Deliberately absent: light.outside_lights, the Govee H706A roofline run.
  // It is fully automated — on at sunset, off at sunrise via
  // `outside_lights_dusk_dawn` in HA (see docs/motion-lighting.md) — so a
  // kiosk tile would only ever be a way to fight the automation. Dakota asked
  // for it to be removed 2026-10-04; do not re-add it. The entity still
  // exists and still works, it just is not a kiosk control.
  //
  // Deliberately absent: Bar (switch.bar_lights, switch.wine_cabinet_lamp),
  // Leather Lamps (switch.small/large_leather_lamp) and Record Player
  // (switch.record_player_light) — Meross gear not yet wired at this house.
  // The old Meross basement lamps (switch.basement_lamps_mss630_*) are
  // superseded by light.basement_1-6 and are no longer referenced.
  //
  // The living room has no smart lights: both "Living NN" bulbs turned out to
  // belong elsewhere (dining room, then the storage room).

  // ---- Outdoor: Ring floodlights + Govee bulbs, held back from bulk-off ----
  // light.outside_light_1-4 are Govee H6008s on the LAN API (added 2026-10-09;
  // names and numbering set by Dakota in HA). They also follow the roofline's
  // sunset/sunrise automation in HA; the tile is here for overrides. They
  // reach 2000K, so STANDARD_KELVIN applies unclamped.
  {
    key: 'outdoor',
    label: 'Outdoor',
    floor: null,
    excludeFromBulkOff: true,
    entities: [
      'light.deck_light',
      'light.patio_light',
      'light.driveway_light',
      'light.outside_light_1',
      'light.outside_light_2',
      'light.outside_light_3',
      'light.outside_light_4',
    ],
  },
];

export const LIGHT_ENTITY_IDS = LIGHT_ROOMS.flatMap((r) => r.entities);

// Tiles are narrow, and the generic noun carries no information when every
// tile is a room: "Master Bedroom" -> "Master", "Upstairs Guest Room" -> "Upstairs Guest".
// "Bathroom" always shortens to "Bath" — house style, here and on the map.
export function shortRoomLabel(label: string): string {
  const short = label
    .replace(/\bBathroom\b/g, 'Bath')
    .replace(/\s+(Bedroom|Room)\b/g, '')
    .trim();
  return short || label;
}

export const LIGHT_FLOOR_ORDER: LightFloorId[] = ['upper', 'main', 'basement'];

// House white. Every colour-capable bulb is driven to the same temperature
// so a room doesn't change character depending on which fixture is on.
//
// 2000K is the floor of the 26 Tuya bulbs, which is nearly everything. The
// three Kasa pantry LB120s only go down to 2700K, but a below-floor request
// CLAMPS rather than failing — verified against the hardware — so they land
// at 2700K and everything else goes properly warm. Bulbs with no colour
// temperature (the Ring floodlights, the dimmable-only Upstairs Guest Room bulb,
// every `switch.*`) just turn on.
// Home Assistant's motion automations turn lights on at this value too — see
// docs/motion-lighting.md. Change one, change the other.
export const STANDARD_KELVIN = 2000;

interface LightEntityState {
  state: string;
  attributes?: { supported_color_modes?: string[] };
}

function supportsColorTemp(st: LightEntityState | undefined): boolean {
  return !!st?.attributes?.supported_color_modes?.includes('color_temp');
}

// A room is on if ANY of its lights is on. `onCount` drives the counts on the
// floor and All-off switches; `offline` is true only when every light in the
// room is unreachable, which is how the boxed-up gear reads today.
export function roomLightState(
  room: LightRoom,
  states: Record<string, LightEntityState | undefined>,
): { on: boolean; onCount: number; offline: boolean } {
  let onCount = 0;
  let offline = 0;
  for (const id of room.entities) {
    const st = states[id]?.state;
    if (st === 'on') onCount++;
    if (!st || st === 'unavailable' || st === 'unknown') offline++;
  }
  return { on: onCount > 0, onCount, offline: offline === room.entities.length };
}

// Every light that's currently on within a set of rooms — the payload for the
// All-off and floor-off switches.
export function litEntities(
  rooms: LightRoom[],
  states: Record<string, LightEntityState | undefined>,
): string[] {
  return rooms.flatMap((r) => r.entities.filter((id) => states[id]?.state === 'on'));
}

export function roomsOnFloor(floor: LightFloorId): LightRoom[] {
  return LIGHT_ROOMS.filter((r) => r.floor === floor);
}

// Rooms the bulk switches are allowed to touch (i.e. not the floodlights).
export function bulkOffRooms(): LightRoom[] {
  return LIGHT_ROOMS.filter((r) => !r.excludeFromBulkOff);
}

// Turn a set of entities on or off.
//
// Calls are grouped three ways, because HA's `light.turn_off` won't accept a
// `switch.*` target and `color_temp_kelvin` is rejected by a light that has
// no colour temperature:
//   switch.*                      -> switch.turn_on / turn_off
//   light.* with colour temp      -> light.turn_on at STANDARD_KELVIN
//   light.* without               -> light.turn_on
// Each group is one round trip however many lights it covers, since the
// proxy takes an entity_id array. `states` is the poll's snapshot, used only
// to read supported_color_modes; without it nothing is sent a temperature.
export async function setLights(
  entityIds: string[],
  on: boolean,
  states?: Record<string, LightEntityState | undefined>,
): Promise<void> {
  if (entityIds.length === 0) return;
  const groups = new Map<string, { domain: string; warm: boolean; ids: string[] }>();
  for (const id of entityIds) {
    const domain = id.split('.')[0];
    if (domain !== 'light' && domain !== 'switch') continue;
    // Colour temperature is only meaningful when turning a bulb ON.
    const warm = on && domain === 'light' && supportsColorTemp(states?.[id]);
    const key = `${domain}:${warm}`;
    const bucket = groups.get(key);
    if (bucket) bucket.ids.push(id);
    else groups.set(key, { domain, warm, ids: [id] });
  }
  await Promise.all(
    [...groups.values()].map(({ domain, warm, ids }) =>
      apiFetch('/api/ha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          service: on ? 'turn_on' : 'turn_off',
          entity_id: ids,
          ...(warm ? { color_temp_kelvin: STANDARD_KELVIN } : {}),
        }),
      }),
    ),
  );
}
